import {
  Component,
  DestroyRef,
  ErrorHandler,
  OnInit,
  inject,
  input,
  InputSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isSentryErrorHandler } from '@techniek-team/sentry-web';
import { ToastService } from '@techniek-team/services';
import { BehaviorSubject, firstValueFrom, merge, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { AlfredClientConfig } from '../../alfred-client.config';
import { AlfredClientService } from '../../alfred-client.service';
import { SessionApi } from '../../api/session/session.api';
import { PostSessionStepRequest } from '../../api/session/session.request';
import { AnswersSelfExam } from '../../models/on-interaction/answers-self-exam.model';
import { GaveAnswer } from '../../models/on-interaction/gave-answer.model';
import { OnInteraction } from '../../models/on-interaction/on-interaction.model';
import { Step } from '../../models/step/step.model';
import { handleInvalidAnswerType } from '../../shared/hand-invalid-answer-type.function';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserApi } from '../../api/user/user.api';
import { User } from '../../models/user.model';
import { isDefined } from '@lyceo/shared/util';
import { StepSupplierComponent } from '../../step-supplier/step-supplier.component';
import { IonSkeletonText } from '@ionic/angular/standalone';
import { AsyncPipe } from '@angular/common';
import { AlfredSessionService } from '../../shared/support-mailto-alfred/alfred-session.service';
import { Session } from '../../models/session.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-questionnaire-handler',
  templateUrl: './questionnaire-handler.component.html',
  styleUrls: ['./questionnaire-handler.component.scss'],
  imports: [StepSupplierComponent, IonSkeletonText, AsyncPipe],
})
export class QuestionnaireHandlerComponent implements OnInit {
  private readonly errorHandler: ErrorHandler = inject(ErrorHandler);
  private readonly sessionApi: SessionApi = inject(SessionApi);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly alfredClientService: AlfredClientService =
    inject(AlfredClientService);
  private readonly config: AlfredClientConfig = inject(AlfredClientConfig);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly userApi: UserApi = inject(UserApi);
  private readonly alfredSessionService: AlfredSessionService =
    inject(AlfredSessionService);

  /**
   * The id of the current questionnaire
   * (set by ion-nav-link)
   */
  public readonly sessionId: InputSignal<string> = input.required();

  /**
   * The observable containing the initial step when opening a questionnaire
   */
  public initialStep$!: Observable<Step | undefined>;

  /**
   * The observable containing the next step (question or thank you page)
   */
  public nextStep$!: Observable<Step | undefined>;

  /**
   * The observable containing the previous step
   */
  public prevStep$!: Observable<Step | undefined>;

  /**
   * The result of combining the firstStep$, nextStep$ and prevStep$
   * observers in a single output
   */
  public currentStep$!: Observable<Step | undefined>;

  /**
   * Disable the next and previous button while fetching the next question
   */
  public isFetching$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true,
  );

  /**
   * The next question that is saved while reviewing the feedback. When
   * the user clicks the next button in the feedback alert, the cached
   * (next) question will be displayed.
   */
  public cachedStep$: BehaviorSubject<Step> = new BehaviorSubject<Step>(
    undefined as never,
  );

  /**
   * The question that is currently being displayed in the template.
   */
  public displayStep$: BehaviorSubject<Step | undefined> = new BehaviorSubject<
    Step | undefined
  >(undefined);

  /**
   * Get the session id from the route and pass it to the
   * session observable
   */
  public ngOnInit(): void {
    this.initialStep$ = this.createFirstStepObserver();

    this.nextStep$ = this.createNextStepObserver();
    this.prevStep$ = this.createPrevStepObserver();

    this.currentStep$ = this.createCurrentQuestionObserver();
    this.getUserProfileAndSession();
  }

  /**
   * Load the cached step.
   *
   * In case of a self exam question also make a post call to the API to save
   * whether or not the user thinks his/her given answer matches the correct
   * answer.
   */
  public async sendSelfExamAnswerToServer(
    interaction: AnswersSelfExam,
  ): Promise<void> {
    if (this.alfredClientService.isApiMode() && interaction.answer) {
      try {
        await firstValueFrom(
          this.sessionApi.patchSelfExamAnswerAsCorrect({
            answerId: interaction.answer.id,
            submittedAnswerAsCorrect: interaction.isCorrect,
          }),
        );
      } catch (error) {
        this.toastService.error(
          'Oeps! We konden niet opslaan of je vond dat je de vraag goed had beantwoord.',
        );

        if (isSentryErrorHandler(this.errorHandler)) {
          this.errorHandler.captureError(error);
        }
      }
    }
  }

  private getUserProfileAndSession(): void {
    this.userApi
      .getUserProfile()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((user: User) => this.alfredSessionService.userData.set(user)),
        switchMap((user: User) => this.sessionApi.getSessions(user.id)),
      )
      .subscribe((sessionList) => {
        const currentSession: Session | undefined = sessionList.sessions.find(
          (session) => session.id === this.sessionId(),
        );
        this.alfredSessionService.sessionData.set(currentSession);
      });
  }

  /**
   * When the user starts or comes back to the current session,
   * fetch the first question, or the question where the user
   * left last time
   */
  private createFirstStepObserver(): Observable<Step | undefined> {
    return this.sessionApi.initialStep(this.sessionId()).pipe(
      catchError((error) => {
        const violations: { message: string }[] = error?.error?.violations;
        const firstViolation: string | undefined = violations?.length
          ? violations[0]?.message
          : undefined;
        this.toastService.error(
          firstViolation || 'Oeps! Jouw test kan niet worden opgehaald!',
        );

        if (isSentryErrorHandler(this.errorHandler)) {
          this.errorHandler.handleError(error);
        }

        return of(undefined);
      }),
      tap(() => this.isFetching$.next(false)),
    );
  }

  /**
   * Subscribe to the goToPrevStep$ subjects that triggers going to the
   * prev step
   */
  private createPrevStepObserver(): Observable<Step | undefined> {
    return this.alfredClientService.onPreviousClick.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => this.isFetching$.next(true)),
      map((previousStep) =>
        this.createRequestBody(previousStep as GaveAnswer, 'previous'),
      ),
      switchMap((changeStepRequest: PostSessionStepRequest) =>
        this.sessionApi.previousStep(this.sessionId(), changeStepRequest).pipe(
          catchError((error) => {
            this.toastService.error(
              'Oeps! De vorige vraag kan niet worden opgehaald!',
            );

            if (isSentryErrorHandler(this.errorHandler)) {
              this.errorHandler.handleError(error);
            }
            return of(undefined);
          }),
        ),
      ),
      tap(() => this.isFetching$.next(false)),
      filter((result) => !!result),
    );
  }

  /**
   * Subscribe to the goToNextStep$ subjects that triggers going to the
   * next step.
   *
   * In case the backend returns feedback for the previous
   * answer. Cache the next question and display the feedback
   * below the previous question. When the user clicks the
   * next button in the feedback box. The cached question
   * will be display.
   *
   * Only the next endpoint will return feedback.
   *
   * Put this logic into a separate method
   */
  private createNextStepObserver(): Observable<Step> {
    return this.alfredClientService.onNextClick.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => this.isFetching$.next(true)),
      switchMap((interaction) => {
        return this.handleInteraction(interaction);
      }),
      tap(() => this.isFetching$.next(false)),
      filter(isDefined),
      map((nextStep: Step) => {
        const displayedStep: Step | undefined = this.displayStep$.getValue();
        // In case the backend returns feedback.
        if (displayedStep && nextStep.feedback) {
          // Return the already visible step and attach the feedback.
          displayedStep.feedback = nextStep.feedback;
          nextStep.feedback = undefined;
          // cache the next step for later.
          this.cachedStep$.next(nextStep);

          return displayedStep;
        }

        // When there is no feedback just display the next step.
        this.cachedStep$.next(undefined as never);
        return nextStep;
      }),
      shareReplay(1),
    );
  }

  /**
   * Inner method used by {@see self.createNextStepObserver} to handle the specific
   * Interactions of the user.
   */
  private handleInteraction<B>(
    interaction: OnInteraction | undefined,
  ): Observable<Step | undefined> {
    if (interaction instanceof GaveAnswer) {
      return this.sessionApi
        .nextStep(this.sessionId(), this.createRequestBody(interaction, 'next'))
        .pipe(
          catchError((error) => {
            this.toastService.error(
              'Oeps! De volgende vraag kan niet worden opgehaald!',
            );

            if (isSentryErrorHandler(this.errorHandler)) {
              this.errorHandler.handleError(error);
            }
            return of(undefined);
          }),
        );
    }
    if (interaction instanceof AnswersSelfExam) {
      this.sendSelfExamAnswerToServer(interaction);
    }
    const cachedStep: Step = this.cachedStep$.getValue();
    this.cachedStep$.next(undefined as never);
    // either on AnswersSelfExam or SeenFeedback we loading the current Cached step.
    return of(cachedStep);
  }

  /**
   * Combine all questions observers in one single output
   * current question observable.
   */
  private createCurrentQuestionObserver(): Observable<Step | undefined> {
    return merge(this.initialStep$, this.nextStep$, this.prevStep$).pipe(
      shareReplay(1),
      tap((step: Step | undefined) => this.displayStep$.next(step)),
    );
  }

  /**
   * Create request body to send to the server.
   */
  private createRequestBody(
    step: GaveAnswer,
    direction: 'next' | 'previous',
  ): PostSessionStepRequest {
    if (!step.answer && direction === 'next') {
      throw new Error('Answer not set on step');
    }

    const request: PostSessionStepRequest = {
      session: `/api/sessions/${this.sessionId()}`,
      question: `/api/questions/${step.question.id}`,
      answer: null,
    };

    if (step.answer === null || step.answer === undefined) {
      return request;
    }

    // Only append the answer if provided.
    if (step.answer.isAcceptableAnswer()) {
      request.answer = {
        id: `/api/answers/${step.answer.id}`,
        type: step.answer.type,
        answer: step.answer.answer,
      };
    } else {
      handleInvalidAnswerType(
        step.answer.answer,
        this.errorHandler,
        this.config.production,
      );
    }

    return request;
  }
}
